import { useCallback, useEffect, useState } from 'react';
import { Table, Button, Modal, Input, message, Tag, Typography, Alert } from 'antd';
import type { TableProps } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import {
  supabase,
  acceptPlatformTrip,
  rejectPlatformTrip,
  formatDate,
  formatTime,
  useFleetOperator,
  usePortalPermissions,
} from '@ubus/shared';
import type { Trip } from '@ubus/shared';
import StatusTag from './StatusTag';
import { TableRowActions } from './TableRowActions';
import { useAdminTableStability } from '../hooks/useAdminTableStability';
import { useTranslation } from 'react-i18next';
import { localeConfirm } from '../utils/localeModal';

const { Text } = Typography;

type PendingTrip = Trip & {
  vehicles?: { fleet_number: string; model: string } | null;
  drivers?: { name: string } | null;
  organizations?: { name: string } | null;
};

/** Platform trips awaiting fleet owner accept/reject — embedded in Jobs. */
export default function PlatformPendingAcceptPanel() {
  const { t } = useTranslation();
  const { operatorId, loading: operatorLoading } = useFleetOperator();
  const { hasPermission } = usePortalPermissions();
  const canAccept = hasPermission('owner_pending.accept');
  const canReject = hasPermission('owner_pending.reject');
  const [trips, setTrips] = useState<PendingTrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [rejectModal, setRejectModal] = useState<PendingTrip | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [acting, setActing] = useState(false);
  const { pagination, onTableChange, afterMutation } = useAdminTableStability();

  const fetchTrips = useCallback(async () => {
    if (!operatorId) {
      setTrips([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('trips')
        .select('*, vehicles(fleet_number, model), drivers(name), organizations(name)')
        .eq('source', 'platform')
        .eq('fleet_operator_id', operatorId)
        .eq('assignment_status', 'pending_owner')
        .order('date', { ascending: true })
        .order('pickup_time', { ascending: true });

      if (error) {
        message.error(error.message);
      } else {
        setTrips((data as PendingTrip[]) ?? []);
      }
    } finally {
      setLoading(false);
    }
  }, [operatorId]);

  useEffect(() => {
    if (!operatorLoading) {
      void fetchTrips();
    }
  }, [fetchTrips, operatorLoading]);

  if (!operatorId || (!loading && trips.length === 0)) {
    return null;
  }

  const handleAccept = (trip: PendingTrip) => {
    localeConfirm(t, {
      title: t('ownerPending.acceptTitle'),
      content: (
        <div>
          <p>
            {t('ownerPending.acceptPrompt', {
              date: formatDate(trip.date),
              time: formatTime(trip.pickup_time),
            })}
          </p>
          <Text type="secondary">
            {t('ownerPending.vehicleDriver', {
              vehicle: trip.vehicles?.fleet_number ?? '—',
              driver: trip.drivers?.name ?? '—',
            })}
          </Text>
        </div>
      ),
      okText: t('ownerPending.accept'),
      onOk: async () => {
        setActing(true);
        try {
          await acceptPlatformTrip(trip.id);
          message.success(t('ownerPending.accepted'));
          await afterMutation(trip.id, fetchTrips);
        } catch (err: unknown) {
          message.error(err instanceof Error ? err.message : t('ownerPending.acceptFailed'));
        } finally {
          setActing(false);
        }
      },
    });
  };

  const handleRejectSubmit = async () => {
    if (!rejectModal) return;
    setActing(true);
    try {
      const rejectedId = rejectModal.id;
      await rejectPlatformTrip(rejectedId, rejectReason.trim() || undefined);
      message.success(t('ownerPending.rejected'));
      setRejectModal(null);
      setRejectReason('');
      await afterMutation(rejectedId, fetchTrips);
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : t('ownerPending.rejectFailed'));
    } finally {
      setActing(false);
    }
  };

  const columns: TableProps<PendingTrip>['columns'] = [
    {
      title: t('ownerPending.date'),
      dataIndex: 'date',
      key: 'date',
      render: (val: string) => formatDate(val),
    },
    {
      title: t('ownerPending.time'),
      key: 'time',
      render: (_, r) => `${formatTime(r.pickup_time)} – ${formatTime(r.end_time)}`,
    },
    {
      title: t('ownerPending.route'),
      key: 'route',
      ellipsis: true,
      render: (_, r) => `${r.pickup_address} → ${r.dropoff_address}`,
    },
    {
      title: t('ownerPending.client'),
      key: 'org',
      render: (_, r) => r.organizations?.name ?? '—',
    },
    {
      title: t('ownerPending.assignment'),
      key: 'assignment',
      render: () => <StatusTag status="pending_owner" />,
    },
    {
      title: t('ownerPending.vehicleDriverColumn'),
      key: 'assign',
      render: (_, r) =>
        r.vehicles && r.drivers ? `${r.vehicles.fleet_number} · ${r.drivers.name}` : '—',
    },
    {
      title: t('ownerPending.actions'),
      key: 'actions',
      fixed: 'right',
      width: 200,
      render: (_, record) => (
        <TableRowActions>
          {canAccept ? (
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              loading={acting}
              onClick={() => handleAccept(record)}
            >
              {t('ownerPending.accept')}
            </Button>
          ) : null}
          {canReject ? (
            <Button
              danger
              size="small"
              icon={<CloseOutlined />}
              onClick={() => {
                setRejectModal(record);
                setRejectReason('');
              }}
            >
              {t('ownerPending.reject')}
            </Button>
          ) : null}
        </TableRowActions>
      ),
    },
  ];

  return (
    <>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          <SpaceInline count={trips.length} label={t('jobs.platformPendingTitle')} />
        }
        description={t('jobs.platformPendingHint')}
      />
      <Table
        size="small"
        style={{ marginBottom: 24 }}
        dataSource={trips}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={pagination}
        onChange={onTableChange}
        locale={{ emptyText: t('ownerPending.empty') }}
      />
      <Modal
        title={t('ownerPending.rejectTitle')}
        open={Boolean(rejectModal)}
        onCancel={() => setRejectModal(null)}
        onOk={() => void handleRejectSubmit()}
        okText={t('ownerPending.reject')}
        okButtonProps={{ danger: true, loading: acting }}
        destroyOnClose
      >
        <p>{t('ownerPending.rejectPrompt')}</p>
        <Input.TextArea
          rows={3}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder={t('ownerPending.reasonPlaceholder')}
        />
      </Modal>
    </>
  );
}

function SpaceInline({ count, label }: { count: number; label: string }) {
  return (
    <span>
      {label} <Tag color="gold">{count}</Tag>
    </span>
  );
}
